import React, { memo } from 'react';
import LinkMenuItem from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { Features } from '../../common/types.tsx';
import { COMMON_ANALYTICS_ATTRIBUTES } from '../service-management-section/constants.tsx';

const headingMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-collection-section.heading',
	defaultMessage: 'Service management apps',
	description: 'Navigation group heading for service management apps settings items',
});

const configurationItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-collection-section.configuration-item',
	defaultMessage: 'Configuration',
	description: 'Navigation item which links to Jira Service Management configuration page',
});

const emailRequestsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-collection-section.email-requests-item',
	defaultMessage: 'Email requests',
	description:
		'Navigation item which links to a Jira Service Management email requests settings page',
});

const knowledgeBasePermissionsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-collection-section.knowledge-base-permissions-item',
	defaultMessage: 'Knowledge base permissions',
	description: 'Navigation item which links to a Jira Service Management Knowledge Base Settings',
});

const authenticationItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-collection-section.authentication-item',
	defaultMessage: 'Authentication',
	description:
		'Navigation item which links to a Jira Service Management Authentication settings page',
});

const complianceSettingsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-collection-section.compliance-settings-item',
	defaultMessage: 'Compliance',
	description: 'Navigation item which links to Jira Service Management compliance page',
});

const organizationsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.service-collection-section.organizations-item',
	defaultMessage: 'Organizations',
	description: 'Navigation item which links to the Organizations List page',
});

type Props = {
	features: Features;
};

const ServiceCollectionSectionInner = ({ features }: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();
	const { licensedProducts } = useTenantContext();
	const hasConfluence = Boolean(licensedProducts[LICENSED_PRODUCTS.CONFLUENCE]);
	const hasEmailRequests = features.hasServiceManagementEmailRequests;
	const hasKnowledgeBasePermissions =
		hasConfluence && features.hasServiceManagementKbPermissionsAccess;
	const hasAuthentication = features.hasServiceManagementAuthenticationSettings;
	const hasOrganizations = features.hasOrganizationsListAccess;

	if (
		!features.hasServiceManagementConfig &&
		!hasEmailRequests &&
		!hasKnowledgeBasePermissions &&
		!hasAuthentication &&
		!hasOrganizations
	) {
		return null;
	}

	return (
		<MenuSection isMenuListItem>
			<MenuSectionHeading>{formatMessage(headingMessage)}</MenuSectionHeading>
			<MenuList>
				{features.hasServiceManagementConfig && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'configuration' }}
						isSelected={currentRoute.name === 'products-jsm-configuration'}
						href="/jira/settings/products/jira-service-management-configuration"
					>
						{formatMessage(configurationItem)}
					</LinkMenuItem>
				)}
				{hasEmailRequests && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'emailRequests' }}
						isSelected={currentRoute.name === 'products-jsm-email-requests'}
						href="/jira/settings/products/jira-service-management-email-requests"
					>
						{formatMessage(emailRequestsItem)}
					</LinkMenuItem>
				)}
				{hasKnowledgeBasePermissions && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'knowledgeBasePermissions' }}
						isSelected={currentRoute.name === 'products-jsm-kb-permissions'}
						href="/jira/settings/products/jira-service-management-knowledge-base-permissions"
					>
						{formatMessage(knowledgeBasePermissionsItem)}
					</LinkMenuItem>
				)}
				{hasAuthentication && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'authentication' }}
						isSelected={currentRoute.name === 'products-jsm-authentication-settings'}
						href="/jira/settings/products/servicedesk/authentication"
					>
						{formatMessage(authenticationItem)}
					</LinkMenuItem>
				)}
				{features.hasServiceManagementConfig && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'complianceSettings' }}
						isSelected={currentRoute.name === 'products-jsm-compliance'}
						href="/jira/settings/products/jira-service-management-compliance"
					>
						{formatMessage(complianceSettingsItem)}
					</LinkMenuItem>
				)}
				{hasOrganizations && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'organizations' }}
						isSelected={currentRoute.name === 'products-jsm-organizations'}
						href="/jira/settings/products/servicedesk/organizations"
					>
						{formatMessage(organizationsItem)}
					</LinkMenuItem>
				)}
			</MenuList>
		</MenuSection>
	);
};

const ServiceCollectionSection: React.MemoExoticComponent<
	({ features }: Props) => React.JSX.Element | null
> = memo(ServiceCollectionSectionInner);
export default ServiceCollectionSection;
